/**
 * system-config 域 Zod 共享契约 — ADM-SYS-S001（地区候选）/ S002（语言候选）
 *
 * 语言 / 地区均为系统级元数据：admin 仅可 toggle `enabled`；主键 / display_name /
 * app_selectable 由发版 seed 维护。故对外只暴露：① 两个只读列表 DTO ② 一个 PATCH enabled 请求。
 */

import { z } from 'zod';

/**
 * language_code：BCP 47 受限子集（dev-docs #824 / C 端 #821）。**单一事实源**——route path-param、
 * display_name_i18n key、apps.allowed_languages 元素都复用本 schema。
 * 写侧唯一合法形态 = canonical：`en` / `pt-BR`（language 小写、REGION 大写）。拒 `pt-br` /
 * script / variant / extension。`pt` 与 `pt-BR` 是两个独立语言。
 * 对齐 `system_languages.language_code` CHECK `^[a-z]{2}(-[A-Z]{2})?$`。
 */
export const LANGUAGE_CODE_RE = /^[a-z]{2}(-[A-Z]{2})?$/;

export const LanguageCodeSchema = z
  .string()
  .regex(LANGUAGE_CODE_RE, 'invalid language code (BCP 47 language or language-REGION)');

/** region_code：ISO 3166-1 alpha-2 大写 2 字符（`system_regions.region_code` DB CHECK `^[A-Z]{2}$`）。 */
export const RegionCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'invalid region code (ISO 3166-1 alpha-2, 2 uppercase letters)');

/** display_name_i18n：language_code → 展示名，至少 1 个 key（契约保证 seed 填 zh/en） */
const DisplayNameI18nSchema = z
  .record(LanguageCodeSchema, z.string().min(1).max(120))
  .refine((m) => Object.keys(m).length > 0, { message: 'display_name_i18n must have ≥1 locale' });

/** PATCH /system-config/(languages|regions)/:code — 仅可改 enabled 启停 */
export const UpdateSystemConfigEnabledRequestSchema = z.object({
  enabled: z.boolean(),
});
export type UpdateSystemConfigEnabledRequest = z.infer<
  typeof UpdateSystemConfigEnabledRequestSchema
>;

/** GET /system-config/languages 列表项（system_languages 行 → 规范化 DTO） */
export type SystemLanguageResponse = {
  /** ISO 639-1 小写码（主键） */
  language_code: string;
  /** 多语言展示名 { en, zh, ... } */
  display_name_i18n: Record<string, string>;
  /** 是否启用（admin 唯一可改字段） */
  enabled: boolean;
  /** 是否可作 App 运行语言被选（只读，seed 维护） */
  app_selectable: boolean;
  /** 该语言书籍定时发布默认时区（IANA tz，只读，seed 维护；ADM-SYS-S002 绑定 / ADM-BOK-S009 定时发布用） */
  default_timezone: string;
  /** 是否锁定 enabled（en 行为 true，不可停用） */
  lock_enabled: boolean;
  /** 最近更新时间 ISO 8601 */
  updated_at: string;
};

/** GET /system-config/regions 列表项（system_regions 行 → 规范化 DTO） */
export type SystemRegionResponse = {
  /** ISO 3166-1 alpha-2 大写码（主键） */
  region_code: string;
  /** 多语言展示名 { en, zh, <local> } */
  display_name_i18n: Record<string, string>;
  /** 是否启用（admin 唯一可改字段） */
  enabled: boolean;
  /** 最近更新时间 ISO 8601 */
  updated_at: string;
  // ⚠️ 不含 updated_by / updated_by_name：dev-docs #823 把这两个字段从 A5 §4 / §6 的
  // SystemRegionResponse 里去掉了 —— PATCH 不写 updated_by、响应不下发它，toggle 的操作员
  // 归属改由既有 audit 事件 update_system_region_enabled 承载（与 §5 SystemLanguageResponse 同口径）。
  // 该列在 C 端 canonical 里仍作为过渡列存在，但 admin 不读它；server 的 contract 刀会 DROP 它。
};

// ─── ADM-FBK-S001 A2 反馈内容翻译开关（admin_settings.feedback_translation_enabled）──────────────

/** 翻译开关 PATCH 请求体 */
export const SetFeedbackTranslationConfigRequestSchema = z.object({
  enabled: z.boolean(),
});
export type SetFeedbackTranslationConfigRequest = z.infer<
  typeof SetFeedbackTranslationConfigRequestSchema
>;

/** 翻译开关读取响应 */
export type FeedbackTranslationConfigResponse = {
  /** 运营开关（默认 true）；为 false 时详情不再自动翻译 */
  enabled: boolean;
  /** DeepL 是否已配置（DEEPL_API_KEY）；false 时即使 enabled=true 翻译也不生效（仅展示用） */
  provider_configured: boolean;
};

// ─── ADM-SYS-S003 运营弹窗 access level（system_popup_access_levels，A5 §11）──────────────

/**
 * 场景闭集与 `ops.ts` 的 `OperationalPopupScenarioSchema` **同值域**（dev-docs §0.2：
 * 「与 `app_operational_popup_scenes.scenario` 同值域」）。这里不 import 复用，是因为
 * 两处的语义主体不同 —— 那边是「某 App 该场景上不上架」，这边是「该场景全系统用哪个
 * access level」；同值域是契约事实，不是实现耦合。任一侧改闭集都要回 dev-docs 改契约，
 * 届时两处一起跟。
 */
export const PopupAccessLevelScenarioSchema = z.enum([
  'payment_exit_retention',
  'first_purchase_offer',
  'insufficient_balance_offer',
]);
export type PopupAccessLevelScenario = z.infer<typeof PopupAccessLevelScenarioSchema>;

/**
 * PUT /system-config/popup-access-levels/:scenario 请求体（A5 §11.2）。
 *
 * `access_level_id` **必填且可为 null**：`null` = 清空该场景配置（此后 C 端
 * `GET /payment/popups` 不返该场景）。用 `.strict()` 拒未知键；不做可售性校验
 * （§11.2「不校验该 access_level_id 是否已有可售 product_metadata 行」）。
 */
export const SetPopupAccessLevelRequestSchema = z
  .object({
    access_level_id: z
      .string()
      .min(1)
      .max(255)
      .regex(/^\S+$/, 'access_level_id must not contain whitespace')
      .nullable(),
  })
  .strict();
export type SetPopupAccessLevelRequest = z.infer<typeof SetPopupAccessLevelRequestSchema>;

/** 单个场景的全局 access level 配置行（A5 §11.1 / §11.2 响应元素） */
export type PopupAccessLevelResponse = {
  scenario: PopupAccessLevelScenario;
  /** null = 该场景未配置，C 端不下发 */
  access_level_id: string | null;
  updated_at: string;
};

export { DisplayNameI18nSchema };
